import React, { useState, useEffect } from 'react';
import { Plus, Check, Star } from 'lucide-react';
import reviewService from '../services/reviewService';

// Helper to extract numeric product ID from Shopify GID
const getProductId = (gid) => {
  if (!gid) return '';
  // Extract numeric ID from format: gid://shopify/Product/123456789
  const match = gid.match(/Product\/(\d+)/);
  return match ? match[1] : '';
};

const ProductCard = ({ product, onAddToCart, onProductClick, hideSubscriptions = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 0, reviewCount: 0, hasReviews: false });

  // Fetch review data when product changes
  useEffect(() => {
    const fetchReviews = async () => {
      const productId = getProductId(product?.id);
      if (productId) {
        const data = await reviewService.getReviewBadge(productId);
        setReviewData(data);
      }
    };
    fetchReviews();
  }, [product?.id]);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    setIsAdded(true);
    onAddToCart?.(product);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleProductClick = () => {
    onProductClick?.(product);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${
          i < Math.floor(rating)
            ? 'fill-amber-400 text-amber-400'
            : i < rating
            ? 'fill-amber-400/50 text-amber-400'
            : 'fill-stone-200 text-stone-200'
        }`}
      />
    ));
  };

  return (
    <div
      className="group relative bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-stone-200/50"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Clickable Image Container */}
      <div 
        className="relative aspect-square bg-gradient-to-br from-stone-50 to-stone-100 overflow-hidden cursor-pointer"
        onClick={handleProductClick}
      >
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-contain p-8 transition-transform duration-700 group-hover:scale-105"
        />
        
        {product.compareAtPrice && (
          <span className="absolute top-4 left-4 bg-rose-500 text-white border-0 text-xs px-2 py-1 rounded-md font-semibold">
            Save ${(parseFloat(product.compareAtPrice) - parseFloat(product.price)).toFixed(0)}
          </span>
        )}

        {/* Subscription Badge - hidden for specific products */}
        {!hideSubscriptions && product.hasSubscription && !['immuno-logic', 'immuno logic', 'good-mood', 'good mood', 'more-chill', 'more chill', 'glucose-defend', 'glucose defend', 'marine-collagen', 'marine collagen', 'whey-protein', 'whey protein', 'cool', 'daily-multi-fiber'].some(term => 
          product.handle?.toLowerCase().includes(term) || product.title?.toLowerCase().includes(term)
        ) && (
          <span className="absolute top-4 right-4 bg-emerald-500 text-white text-xs px-2 py-1 rounded-md font-semibold">
            Subscribe & Save
          </span>
        )}

        <div
          className={`absolute bottom-4 left-4 right-4 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <button
            onClick={handleAddToCart}
            data-testid={`quick-add-${product.handle}`}
            className={`w-full rounded-xl py-4 text-sm font-medium transition-all duration-300 flex items-center justify-center ${
              isAdded
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                : 'bg-stone-900 hover:bg-stone-800 text-white'
            }`}
          >
            {isAdded ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Added to Cart
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add to Cart
              </>
            )}
          </button>
        </div>
      </div>

      <div className="p-5">
        <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">
          {product.vendor}
        </p>

        {/* Clickable Title */}
        <h3 
          className="text-lg font-medium text-stone-800 mb-2 group-hover:text-amber-700 transition-colors cursor-pointer hover:underline"
          onClick={handleProductClick}
        >
          {product.title}
        </h3>

        {/* Review Stars */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-0.5">
            {renderStars(reviewData.rating)}
          </div>
          {reviewData.hasReviews && (
            <span className="text-xs text-stone-400">({reviewData.reviewCount})</span>
          )}
        </div>

        {/* Show subtitle metafield if available, otherwise fall back to description */}
        <p className="text-sm text-stone-500 line-clamp-2 mb-4">
          {product.subtitle || product.description}
        </p>

        <div className="flex items-center gap-2">
          <span className="text-xl font-semibold text-stone-900">
            ${product.price}
          </span>
          {product.compareAtPrice && (
            <span className="text-sm text-stone-400 line-through">
              ${product.compareAtPrice}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
